import type { Pagination } from '@detect/shared';
import { getFirebaseAuth } from '../config/firebase';
import { environment } from '../config/environment';
import { recordApiRequest } from './feedbackCapture';

/**
 * Base API client (frontend-patterns §5, adapted): standard envelope,
 * typed errors, tenant threading via query param. All server state
 * flows through here — components never fetch() directly.
 */

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  pagination?: Pagination;
  error?: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getAuthToken(): Promise<string> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new ApiError('Not authenticated', 401, 'UNAUTHENTICATED');
  return user.getIdToken();
}

export interface PaginatedResult<T> {
  items: T[];
  pagination: Pagination | null;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ data: T; pagination: Pagination | null }> {
  const token = await getAuthToken();
  const startedAt = Date.now();
  const method = options.method ?? 'GET';
  let response: Response;
  try {
    response = await fetch(`${environment.apiUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  } catch (err) {
    recordApiRequest({ method, path: endpoint, status: 0, durationMs: Date.now() - startedAt });
    throw err;
  }
  // Method, path, status and duration only (never a body or a header): the feedback widget's recent-activity log.
  recordApiRequest({ method, path: endpoint, status: response.status, durationMs: Date.now() - startedAt });
  if (response.status === 204) {
    return { data: undefined as T, pagination: null };
  }
  const body = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!response.ok || !body || body.success !== true) {
    throw new ApiError(
      body?.error?.message ?? `Request failed (HTTP ${response.status})`,
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.details
    );
  }
  return { data: body.data as T, pagination: body.pagination ?? null };
}

/** Append tenantId (and extra params) to an endpoint. */
export function withTenant(
  endpoint: string,
  tenantId: string,
  params: Record<string, string | number | undefined> = {}
): string {
  const query = new URLSearchParams({ tenantId });
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const sep = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${sep}${query.toString()}`;
}

export const api = {
  get: async <T>(endpoint: string): Promise<T> => (await request<T>(endpoint)).data,
  getPaginated: async <T>(endpoint: string): Promise<PaginatedResult<T>> => {
    const { data, pagination } = await request<T[]>(endpoint);
    return { items: data, pagination };
  },
  post: async <T>(endpoint: string, body?: unknown): Promise<T> =>
    (
      await request<T>(endpoint, {
        method: 'POST',
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    ).data,
  put: async <T>(endpoint: string, body: unknown): Promise<T> =>
    (await request<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) })).data,
  patch: async <T>(endpoint: string, body: unknown): Promise<T> =>
    (await request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) })).data,
  delete: async (endpoint: string): Promise<void> => {
    await request<void>(endpoint, { method: 'DELETE' });
  },
};
